/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Input, Label, Spacer, Switch, Text, theme } from "@nordcloud/gnui";
import { Fieldset } from "~/components/Forms";
import FlexContainer from "~/components/Styled/FlexContainer";
import { useCustomer } from "~/context/User/Customer";
import { Errors, Register, SetValue, Watcher } from "~/models/reactHookForm";
import { ROUTES } from "~/routing";
import {
  isValidPrice,
  ValidationMessagePrice,
  displayCurrencySymbol,
} from "~/tools";

export const StyledFieldset = styled(Fieldset)`
  width: 18rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledTextWrapper = styled.div`
  min-height: 1.8rem;
`;

type SwitchToDefaultValueProps = {
  label: string;
  name: string;
  nameDefault: string;
  defaultPrice: string;
  watcher: Watcher;
  setValue: SetValue;
  register: Register;
  price: string;
  errors: Errors;
  canEdit?: boolean;
};

function SwitchToDefaultValue({
  label,
  name,
  nameDefault,
  defaultPrice,
  watcher,
  setValue,
  register,
  price,
  errors,
  canEdit = true,
}: SwitchToDefaultValueProps) {
  const InputSettings = register({
    validate: isValidPrice,
  });

  const { customer } = useCustomer();

  return (
    <StyledFieldset>
      <Label htmlFor={name} name={label} />
      <Spacer height="0.5rem" />
      {defaultPrice && canEdit ? (
        <Switch
          position="right"
          labelText="Use Default Value"
          name={nameDefault}
          checked={watcher[nameDefault]}
          onChange={(e) => {
            setValue(nameDefault, e.target.checked);
          }}
        />
      ) : (
        <Text weight="medium" size="sm" tag="div">
          To set custom hourly rate, first please set default hourly rate in{" "}
          <Link to={`${ROUTES.customers.index}/${customer.id}/billing-rules`}>
            Billing Rules
          </Link>
        </Text>
      )}
      <Spacer height="1rem" />
      {watcher[nameDefault] ? (
        <StyledTextWrapper key={nameDefault}>
          <Text size="md" tag="div" css={{ paddingTop: "1.2rem" }}>
            {defaultPrice && `Default Value: `}
            <Text size="md" tag="span" color={theme.colors.success}>
              {defaultPrice &&
                `${defaultPrice} ${displayCurrencySymbol(
                  customer?.info?.currency
                )}`}
            </Text>
          </Text>
          <input
            type="hidden"
            name={name}
            disabled
            defaultValue={defaultPrice}
            ref={InputSettings}
          />
        </StyledTextWrapper>
      ) : (
        <FlexContainer css={{ width: "100%" }} key={name}>
          {canEdit ? (
            <>
              <Input
                id={name}
                type="number"
                name={name}
                disabled={watcher[nameDefault]}
                defaultValue={price ? price : defaultPrice}
                placeholder="price/h"
                status={errors[name] && "danger"}
                ref={InputSettings}
              />
              <Text size="md" tag="div" css={{ marginLeft: "1rem" }}>
                {displayCurrencySymbol(customer?.info?.currency)}/h
              </Text>
            </>
          ) : (
            <StyledTextWrapper key={nameDefault}>
              <Text size="md" tag="div" css={{ paddingTop: "1.2rem" }}>
                Value:
                <Text size="md" tag="span" color={theme.colors.success}>
                  {` ${price} ${displayCurrencySymbol(
                    customer?.info?.currency
                  )}`}
                </Text>
              </Text>
              <input
                type="hidden"
                name={name}
                disabled
                defaultValue={defaultPrice}
                ref={InputSettings}
              />
            </StyledTextWrapper>
          )}
        </FlexContainer>
      )}
      {errors[name] ? <ValidationMessagePrice /> : <Spacer height="2.5rem" />}
    </StyledFieldset>
  );
}

export { SwitchToDefaultValue };
